import {
  useCallback,
  useMemo,
  useRef,
  useState,
  memo,
  useContext,
} from 'react';
import { Button, Input, Rating } from '@appsumo/dorado-react';

import { Survey } from '~/types/survey/types';
import { SurveyContext } from '~/contexts/survey';
import { SurveyContainer } from '~/components/survey/SurveyContainer';

export default memo(function NPSComponent({
  survey,
}: Readonly<{ survey: Survey }>) {
  const [selectedValue, setSelectedValue] = useState<number | undefined>(
    undefined,
  );
  const [feedback, setFeedback] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const feedbackInputRef = useRef<HTMLInputElement | null>(null);
  const { isProcessing, submit, dismiss } = useContext(SurveyContext);

  const question = useMemo(() => {
    return survey.questions[0];
  }, [survey.questions]);

  const title = useMemo(() => {
    return isSubmitted
      ? 'Thank you! Your response has been recorded'
      : question.text;
  }, [isSubmitted, question.text]);

  const ratingLabels = useMemo(() => {
    const choices = question.choices;

    return {
      first: choices[0].label,
      last: choices[choices.length - 1].label,
    };
  }, [question.choices]);

  // Get the min and and max values from the choices setup. This assumes all the choices are in order.
  const ratingMinMax = useMemo(() => {
    const choices = question.choices;

    return {
      min: Number(choices[0].value),
      max: Number(choices[choices.length - 1].value),
    };
  }, [question.choices]);

  const followUpQuestion = useMemo(() => {
    return question.choices.find(
      (choice) => Number(choice.value) === selectedValue,
    )?.follow_up_question;
  }, [question.choices, selectedValue]);

  const isInputRequired = useMemo(() => {
    return !!followUpQuestion?.required;
  }, [followUpQuestion?.required]);

  const onChange = useCallback((value: number) => {
    setSelectedValue(value);

    // Need to focus on the input field after the form is shown.
    // useEffect did not work as expected
    setTimeout(() => feedbackInputRef.current?.focus());
  }, []);

  const onSubmit = useCallback(() => {
    submit({
      id: survey.id,
      questionId: question.id,

      // Sending -1 if the choice is not found to prevent ts linting warnings. This should never happen.
      choiceId:
        question.choices.find(
          (choice) => Number(choice.value) === selectedValue,
        )?.id ?? -1,
      followUpQuestionId: followUpQuestion?.id,
      text: feedback,
    });
    setIsSubmitted(true);
  }, [
    feedback,
    followUpQuestion,
    question.choices,
    question.id,
    selectedValue,
    submit,
    survey.id,
  ]);

  const onDismiss = useCallback(() => {
    dismiss(survey.id);
  }, [dismiss, survey.id]);

  return (
    <SurveyContainer
      title={title}
      isSubmitted={isSubmitted}
      className="bottom-0 left-0 w-full md:bottom-[18px] md:left-[13px]"
      onClose={onDismiss}
    >
      <div className="flex flex-col gap-y-2">
        <Rating
          variant="bolt"
          min={ratingMinMax.min}
          max={ratingMinMax.max}
          className="justify-center md:justify-start"
          disabled={isSubmitted}
          onChange={(value) => {
            onChange(value);
          }}
        />

        <div className="flex justify-between">
          <span>{ratingLabels.first} </span>
          <span>{ratingLabels.last} </span>
        </div>

        {!isSubmitted && (
          <div>
            {followUpQuestion && (
              <Input
                data-testid="feedback-input"
                ref={feedbackInputRef}
                inputId="feedback-input"
                name="feedback-input"
                type="text"
                label={followUpQuestion.text}
                required={isInputRequired}
                onChange={(e) => setFeedback(e.target.value)}
              />
            )}

            {Number(selectedValue) >= ratingMinMax.min && (
              <div className="text-right">
                <Button
                  className="mt-4 w-full md:w-auto"
                  onClick={() => {
                    onSubmit();
                  }}
                  disabled={isProcessing || (isInputRequired && !feedback)}
                >
                  Submit
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </SurveyContainer>
  );
});
